import React from 'react'
import { Icon } from './icons'
import { Dropdown } from './shared'
import type { QuickActionRequest, Role, Server, User } from './types'

type RequiredRole = 'admin' | 'owner' | 'all'

interface QACardProps {
  icon: string
  title: string
  role?: RequiredRole
  currentRole: Role
  cmd?: string
  onSubmit: () => void
  submitLabel?: string
  disabled?: boolean
  children?: React.ReactNode
}

export function QACard({ icon, title, role, children, cmd, onSubmit, submitLabel = 'Send', currentRole, disabled }: QACardProps) {
  const rolePill = role === 'admin' ? <span className="chip warn">admin+</span> :
    role === 'owner' ? <span className="chip err">owner</span> : null

  const canUse = !role || role === 'all' ||
    (role === 'admin' && (currentRole === 'admin' || currentRole === 'owner')) ||
    (role === 'owner' && currentRole === 'owner')

  if (!canUse) return null

  return (
    <div className="qa-card">
      <div className="qa-head">
        <Icon name={icon} size={16} className="ico" />
        <h4>{title}</h4>
        {rolePill && <span style={{ marginLeft: 'auto' }}>{rolePill}</span>}
      </div>
      {children}
      <div className="qa-cmd"><span className="caret">/</span><span>{cmd || '--'}</span></div>
      <div className="qa-foot">
        <button className="btn primary" onClick={onSubmit} disabled={disabled}>{submitLabel}</button>
      </div>
    </div>
  )
}

interface QuickActionsTabProps {
  server: Server
  role: Role
  viewer: User
  onRunAction: (request: QuickActionRequest) => void
}

export function QuickActionsTab({ server, role, viewer, onRunAction }: QuickActionsTabProps) {
  const [sayText, setSayText] = React.useState('hi everyone')
  const [gmTarget, setGmTarget] = React.useState(server.players[0] || (viewer.mcUsername ?? ''))
  const [gmMode, setGmMode] = React.useState('creative')
  const [timeValue, setTimeValue] = React.useState('day')
  const [weather, setWeather] = React.useState('clear')
  const [weatherDuration, setWeatherDuration] = React.useState(600)
  const [rule, setRule] = React.useState('keepInventory')
  const [ruleValue, setRuleValue] = React.useState(true)
  const [tpFrom, setTpFrom] = React.useState(server.players[0] || (viewer.mcUsername ?? ''))
  const [tpTo, setTpTo] = React.useState(server.players[1] || (viewer.mcUsername ?? ''))

  React.useEffect(() => {
    setGmTarget(server.players[0] || (viewer.mcUsername ?? ''))
    setTpFrom(server.players[0] || (viewer.mcUsername ?? ''))
    setTpTo(server.players[1] || (viewer.mcUsername ?? ''))
  }, [server.id, server.players, (viewer.mcUsername ?? '')])

  if (server.status !== 'online') {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
        <Icon name="power" size={28} style={{ opacity: 0.4, marginBottom: 10 }} />
        <h3 style={{ margin: '0 0 4px' }}>Quick Actions unavailable</h3>
        <div className="dim text-sm">Bring {server.name} online before sending RCON templates.</div>
      </div>
    )
  }

  return (
    <>
      <div className="qa-grid">
        <QACard
          icon="message-square"
          title="Say"
          cmd={`<${viewer.displayName}> ${sayText || '<text>'}`}
          currentRole={role}
          onSubmit={() => onRunAction({ template: 'say', payload: { text: sayText } })}
          disabled={!sayText.trim()}
        >
          <div className="qa-desc">Broadcast to all online players using your panel display name.</div>
          <textarea rows={2} value={sayText} onChange={event => setSayText(event.target.value)} />
        </QACard>

        <QACard
          icon="heart"
          title="Heal me"
          cmd={`effect give ${(viewer.mcUsername ?? '')} minecraft:instant_health 1 10`}
          currentRole={role}
          onSubmit={() => onRunAction({ template: 'heal_self' })}
        >
          <div className="qa-desc">Full HP for you (<span className="mono">{(viewer.mcUsername ?? '')}</span>).</div>
          <div className="text-xs dim" style={{ padding: '10px 0' }}>
            Uses your saved MC username mapping.
          </div>
        </QACard>

        <QACard
          icon="clock"
          title="TP me home"
          cmd={`tp ${(viewer.mcUsername ?? '')} ${viewer.home ?? '0 64 0'}`}
          currentRole={role}
          onSubmit={() => onRunAction({ template: 'tp_home', payload: { home: viewer.home ?? '0 64 0' } })}
        >
          <div className="qa-desc">Teleport to your registered home coordinates.</div>
          <div className="text-xs mono dim" style={{ padding: '10px 0' }}>
            home: {viewer.home ?? 'not configured'}
          </div>
        </QACard>

        <QACard
          icon="gamepad"
          title="Gamemode (other)"
          role="admin"
          cmd={`gamemode ${gmMode} ${gmTarget}`}
          currentRole={role}
          onSubmit={() => onRunAction({ template: 'gamemode_other', payload: { mode: gmMode, target: gmTarget } })}
          disabled={!gmTarget}
        >
          <div className="qa-desc">Change another player's mode.</div>
          <div className="field-row">
            <label>Target</label>
            <Dropdown
              value={gmTarget}
              onChange={setGmTarget}
              options={[...new Set([(viewer.mcUsername ?? ''), ...server.players])].map(player => ({ value: player, label: player }))}
            />
          </div>
          <div className="field-row">
            <label>Mode</label>
            <div className="seg-radio">
              {['survival', 'creative', 'adventure', 'spectator'].map(mode =>
                <button key={mode} className={gmMode === mode ? 'active' : ''} onClick={() => setGmMode(mode)}>{mode}</button>,
              )}
            </div>
          </div>
        </QACard>

        <QACard
          icon="sun"
          title="Time"
          role="admin"
          cmd={`time set ${timeValue}`}
          currentRole={role}
          onSubmit={() => onRunAction({ template: 'time', payload: { value: timeValue } })}
        >
          <div className="qa-desc">Set the in-world time.</div>
          <div className="seg-radio">
            {['day', 'noon', 'night', 'midnight'].map(value =>
              <button key={value} className={timeValue === value ? 'active' : ''} onClick={() => setTimeValue(value)}>{value}</button>,
            )}
          </div>
        </QACard>

        <QACard
          icon={weather === 'clear' ? 'sun' : weather === 'rain' ? 'cloud-rain' : 'cloud-lightning'}
          title="Weather"
          role="admin"
          cmd={`weather ${weather} ${weatherDuration}`}
          currentRole={role}
          onSubmit={() => onRunAction({ template: 'weather', payload: { kind: weather, duration: weatherDuration } })}
        >
          <div className="qa-desc">Clear, rain, or thunder with a bounded duration.</div>
          <div className="seg-radio">
            {['clear', 'rain', 'thunder'].map(value =>
              <button key={value} className={weather === value ? 'active' : ''} onClick={() => setWeather(value)}>{value}</button>,
            )}
          </div>
          <div className="field-row">
            <label>Duration <span className="mono dim">({weatherDuration}s)</span></label>
            <input type="range" min="60" max="6000" step="60" value={weatherDuration} onChange={event => setWeatherDuration(Number(event.target.value))} style={{ padding: 0, border: 'none', background: 'transparent' }} />
          </div>
        </QACard>

        <QACard
          icon="file-code"
          title="Gamerule"
          role="admin"
          cmd={`gamerule ${rule} ${ruleValue}`}
          currentRole={role}
          onSubmit={() => onRunAction({ template: 'gamerule', payload: { rule, value: ruleValue } })}
        >
          <div className="qa-desc">Toggle a server-wide gamerule.</div>
          <div className="field-row">
            <label>Rule</label>
            <Dropdown
              value={rule}
              onChange={setRule}
              options={['keepInventory', 'doDaylightCycle', 'doWeatherCycle', 'mobGriefing', 'pvp', 'fallDamage', 'fireDamage'].map(item => ({ value: item, label: item }))}
            />
          </div>
          <div className="field-row">
            <label>Value</label>
            <div className="seg-radio">
              <button className={ruleValue ? 'active' : ''} onClick={() => setRuleValue(true)}>true</button>
              <button className={!ruleValue ? 'active' : ''} onClick={() => setRuleValue(false)}>false</button>
            </div>
          </div>
        </QACard>

        <QACard
          icon="external-link"
          title="Teleport"
          role="admin"
          cmd={`tp ${tpFrom} ${tpTo}`}
          currentRole={role}
          onSubmit={() => onRunAction({ template: 'tp_player', payload: { from: tpFrom, to: tpTo } })}
          disabled={!tpFrom || !tpTo}
        >
          <div className="qa-desc">Move a player to another player.</div>
          <div className="field-row horiz">
            <label>From</label>
            <Dropdown
              style={{ flex: 1 }}
              value={tpFrom}
              onChange={setTpFrom}
              options={[...new Set([(viewer.mcUsername ?? ''), ...server.players])].map(player => ({ value: player, label: player }))}
            />
          </div>
          <div className="field-row horiz">
            <label>To</label>
            <Dropdown
              style={{ flex: 1 }}
              value={tpTo}
              onChange={setTpTo}
              options={[...new Set([(viewer.mcUsername ?? ''), ...server.players])].map(player => ({ value: player, label: player }))}
            />
          </div>
        </QACard>

      </div>
    </>
  )
}

export function PhaseTabStub({ name }: { name: string }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '56px 20px' }}>
      <Icon name="git-branch" size={26} style={{ opacity: 0.4, marginBottom: 10 }} />
      <h3 style={{ margin: '0 0 4px' }}>{name} - Phase 2</h3>
      <div className="dim text-sm">This tab is roadmap'd but not yet implemented.</div>
      <div className="text-xs dim mt-2">The top-level page exists, but this server-scoped tab still needs a dedicated design.</div>
    </div>
  )
}
